import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { DownloadRecord } from '@/types';

const GUEST_HISTORY_CAP = 50;

interface DownloadState {
  /** Active jobs shown in the processing queue (optimistic + live). */
  queue: DownloadRecord[];
  /** Local history for guests (persisted — signed URLs stay valid for 24h). */
  guestHistory: DownloadRecord[];
  upsertQueueItem: (record: DownloadRecord) => void;
  removeQueueItem: (id: string) => void;
  /** Replace queue with server records, keeping unmatched optimistic locals. */
  syncQueue: (records: DownloadRecord[]) => void;
  addGuestRecord: (record: DownloadRecord) => void;
  removeGuestRecord: (id: string) => void;
  clearGuestHistory: () => void;
}

export const useDownloadStore = create<DownloadState>()(
  persist(
    (set) => ({
      queue: [],
      guestHistory: [],
      upsertQueueItem: (record) =>
        set((state) => {
          const exists = state.queue.some((item) => item.id === record.id);
          return {
            queue: exists
              ? state.queue.map((item) => (item.id === record.id ? record : item))
              : [record, ...state.queue],
          };
        }),
      removeQueueItem: (id) =>
        set((state) => ({ queue: state.queue.filter((item) => item.id !== id) })),
      syncQueue: (records) =>
        set((state) => {
          const locals = state.queue.filter(
            (item) =>
              item.id.startsWith('local-') &&
              !records.some(
                (record) =>
                  record.sourceUrl === item.sourceUrl && record.outputType === item.outputType
              )
          );
          return { queue: [...locals, ...records] };
        }),
      addGuestRecord: (record) =>
        set((state) => ({
          guestHistory: [record, ...state.guestHistory.filter((item) => item.id !== record.id)].slice(
            0,
            GUEST_HISTORY_CAP
          ),
        })),
      removeGuestRecord: (id) =>
        set((state) => ({ guestHistory: state.guestHistory.filter((item) => item.id !== id) })),
      clearGuestHistory: () => set({ guestHistory: [] }),
    }),
    {
      name: 'dmmt-downloads',
      partialize: (state) => ({ guestHistory: state.guestHistory }),
    }
  )
);
